<template>
  <main>
    <JobViewOrEditHeader :job="job" is-editing @click="$emit('previousStep')" />

    <JobViewOrEditDescription>
      <div class="border-l-2 border-black">
        <TextEditor
          v-model="description"
          data-cy="description-text-editor"
          :placeholder="$t('create.step3.description_placeholder').toString()"
        />
      </div>
    </JobViewOrEditDescription>

    <JobViewOrEditApplication>
      <RichText class="instructions mb-8 cursor-pointer border border-dashed border-black transition-colors duration-200 hover:bg-black/10">
        <TextEditor
          v-model="applicationInstructions"
          data-cy="application-instructions-text-editor"
          :placeholder="$t('create.step3.application_instructions_placeholder').toString()"
        />
      </RichText>

      <button class="btn-primary-color-m mb-8">
        {{ $t('cta_buttons.apply_online') }}
      </button>

      <FormCheckboxField id="terms" v-model="terms" :validation="$v.terms">
        {{ $t('create.accept') }}

        <a class="text-blue underline-offset-2 hover:underline" :href="termsAndConditionsLink()" target="_blank">
          {{ $t('create.terms') }}
        </a>
      </FormCheckboxField>
    </JobViewOrEditApplication>

    <JobWizardFooter
      :job-offer-price="jobOfferPrice || undefined"
      :show-price="isNewJob"
      :step-count="stepCount"
      :current-step-count="currentStepCount"
      :validator="$v"
      @next-step="$emit('nextStep')"
      @previous-step="$emit('previousStep')"
    />
  </main>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import JobWizardFooter from '@/components/CreateJob/JobWizardFooter.vue'
import FormCheckboxField from '@/components/Form/FormCheckboxField.vue'
import RichText from '@/components/JobBoard/RichText.vue'
import JobViewOrEditApplication from '@/components/JobViewOrEditApplication.vue'
import JobViewOrEditDescription from '@/components/JobViewOrEditDescription.vue'
import JobViewOrEditHeader from '@/components/JobViewOrEditHeader.vue'
import TextEditor from '@/components/TextEditor.vue'

import { useRuntimeConfig } from '#app'
import { useNewJobStore } from '~/store/newJob'

defineProps<{
  isNewJob?: boolean
  stepCount: number
  currentStepCount: number
}>()

defineEmits(['nextStep', 'previousStep'])

const { t, locale } = useI18n()
const store = useNewJobStore()

const {
  public: { rootDomain },
} = useRuntimeConfig()

const terms = ref(false)

const termsAndConditionsLink = () => {
  return locale.value === 'en' ? `${rootDomain}/${locale.value}/terms-conditions` : `${rootDomain}/${locale.value}/agb`
}

const jobOfferPrice = computed(() => {
  return store.jobOfferPrice
})

const description = computed({
  get() {
    return store.jobOffer.description || ''
  },
  set(value) {
    store.updateJobOffer({ description: value })
  },
})
const applicationInstructions = computed({
  get() {
    return store.jobOffer.applicationInstructions
  },
  set(value) {
    store.updateJobOffer({
      applicationInstructions: value,
    })
  },
})

const job = computed(() => {
  return {
    ...store.jobOffer,
    locations: store.locations,
    experiences: store.jobOffer.experience,
  }
})

const trueValidator = (value: boolean) => {
  return value === true
}

const rules = {
  description: { required },
  applicationInstructions: { required },
  terms: {
    trueValidator: helpers.withMessage(t('create.validation.terms'), trueValidator),
  },
}

const $v = useVuelidate(rules, {
  description,
  applicationInstructions,
  terms,
})
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.instructions :deep(.ck-editor__editable_inline) {
  @apply h-36;
}
</style>
